import React from "react";
import BartenderHead from "./BartenderHead";

/**
 * Immersive AI-Pick bar. The whole screen reads as a warm wood bar: pendant
 * lights overhead, backlit shelves packed wall-to-wall with bottles, a RUSH
 * neon sign on the back wall, the AI bartender standing behind a full-width
 * wooden counter, and his speech bubble floating above his head. Stage content
 * (recommendation cards, shaker loader, CTA) rests on the counter via `tray`.
 *
 * Counter dressing — a warm table lamp plus a couple of liquor bottles — sits
 * only on the LEFT edge; the right side is intentionally left bare so the space
 * in front of the bartender stays clear for the bubble tail and tray content.
 *
 * Styling lives in index.css under the `.bar-scene` block. The scene is
 * `position: absolute; inset: 0`, so it must be placed inside a positioned,
 * full-bleed container (the AI-Pick page strips its padding for these stages).
 */

type ShelfItem = { kind: "bottle" | "glass"; h: number; tint: string };

// Back-bar glass palette — amber/brown spirits, a couple of RUSH-green bottles,
// a few clear/blue and deep-red ones, so the shelves read as a fully stocked bar.
const BOTTLE_TINTS = [
  "rgba(150,86,40,0.92)",
  "rgba(96,124,58,0.9)",
  "rgba(184,120,52,0.9)",
  "rgba(74,52,30,0.92)",
  "rgba(206,176,128,0.82)",
  "rgba(120,150,70,0.85)",
  "rgba(168,96,40,0.9)",
  "rgba(140,40,52,0.9)",
  "rgba(70,110,150,0.78)",
  "rgba(210,205,196,0.7)",
];

// Deterministic mixed row of bottles + glasses — every 4th item is a glass,
// heights/tints fan out so the shelf doesn't look uniform. Heights are a % of
// the shelf row height (set in CSS) so the shelf scales with the screen.
function shelfItems(count: number, base: number): ShelfItem[] {
  return Array.from({ length: count }, (_, i) => {
    if ((i + base) % 4 === 3) {
      return { kind: "glass" as const, h: 30 + ((i * 7 + base) % 14), tint: "" };
    }
    return {
      kind: "bottle" as const,
      h: 56 + ((i * 11 + base) % 38),
      tint: BOTTLE_TINTS[(i + base) % BOTTLE_TINTS.length],
    };
  });
}

// Three shelves, densely packed so the back bar reads as "completely filled".
const SHELF_ROWS: ShelfItem[][] = [
  shelfItems(34, 0),
  shelfItems(36, 2),
  shelfItems(34, 1),
];

type BottleKind = "wine" | "vodka" | "whiskey";

// Counter bottles — kind + height (% of the side strip). A warm table lamp
// stands at the far-left edge with two bottles beside it; the beer tower owns
// the right edge on its own. Everything is kept hard against the side walls so
// the centre — directly in front of the bartender's face and body — stays
// completely clear (verified: no prop overlaps his head/torso down to 360px).
const LEFT_COUNTER: Array<{ kind: BottleKind; h: number }> = [
  { kind: "whiskey", h: 78 },
  { kind: "wine", h: 96 },
];

function ShelfPiece({ item }: { item: ShelfItem }) {
  if (item.kind === "glass") {
    return <div className="bar-scene__glass" style={{ height: `${item.h}%` }} />;
  }
  return (
    <div
      className="bar-scene__bottle"
      style={{
        height: `${item.h}%`,
        background: `linear-gradient(180deg, ${item.tint}, rgba(255,255,255,0.04))`,
      }}
    />
  );
}

/** A single realistic liquor bottle standing on the counter (wine / vodka /
 *  whiskey). Flat fills only — no SVG filters (expensive on low-power GPUs). */
function CounterBottle({
  kind,
  style,
}: {
  kind: BottleKind;
  style?: React.CSSProperties;
}) {
  const common = (
    <>
      {/* shadow on the bar */}
      <ellipse cx="13" cy="82.5" rx="11" ry="2.2" fill="#000" opacity="0.3" />
    </>
  );
  if (kind === "wine") {
    return (
      <svg className="bar-scene__counter-bottle" viewBox="0 0 26 84" style={style} aria-hidden>
        {common}
        {/* long neck → sloped shoulders → tall body, deep-green glass */}
        <rect x="11" y="6" width="4" height="22" rx="1.6" fill="#234d31" />
        <path d="M11 27 Q9 34 7.5 42 L7.5 76 Q7.5 81 13 81 Q18.5 81 18.5 76 L18.5 42 Q17 34 15 27 Z" fill="#1f4a2e" />
        <rect x="6.6" y="50" width="12.8" height="18" rx="1.5" fill="#e9e0c8" opacity="0.92" />
        <rect x="10.4" y="3" width="5.2" height="4" rx="1" fill="#7a1322" />
        <rect x="9" y="44" width="2" height="30" rx="1" fill="#ffffff" opacity="0.18" />
      </svg>
    );
  }
  if (kind === "vodka") {
    return (
      <svg className="bar-scene__counter-bottle" viewBox="0 0 26 84" style={style} aria-hidden>
        {common}
        {/* straight tall clear bottle */}
        <rect x="10.5" y="6" width="5" height="16" rx="1.4" fill="#cfe2ec" opacity="0.85" />
        <rect x="7" y="22" width="12" height="59" rx="3" fill="#bcd6e6" opacity="0.82" />
        <rect x="7" y="40" width="12" height="22" rx="1.5" fill="#eef4f8" opacity="0.9" />
        <rect x="10.2" y="3.5" width="5.6" height="3.5" rx="1" fill="#3a4654" />
        <rect x="8.4" y="26" width="2" height="50" rx="1" fill="#ffffff" opacity="0.5" />
      </svg>
    );
  }
  // whiskey — shorter, broad square-shouldered amber bottle
  return (
    <svg className="bar-scene__counter-bottle" viewBox="0 0 26 84" style={style} aria-hidden>
      {common}
      <rect x="10" y="10" width="6" height="16" rx="1.4" fill="#7a4316" />
      <path d="M6 28 Q6 26 8 26 L18 26 Q20 26 20 28 L20 78 Q20 81 16 81 L10 81 Q6 81 6 78 Z" fill="#9a531c" />
      <rect x="6.4" y="46" width="13.2" height="20" rx="1.5" fill="#f0e2c4" opacity="0.94" />
      <rect x="9.6" y="6.5" width="6.8" height="4" rx="1" fill="#2a1a0c" />
      <rect x="8.5" y="30" width="2" height="34" rx="1" fill="#ffe6b0" opacity="0.3" />
    </svg>
  );
}

/** Small warm bar table-lamp (base · stem · glowing shade). Sides only. */
function TableLamp({ style }: { style?: React.CSSProperties }) {
  return (
    <svg className="bar-scene__table-lamp" viewBox="0 0 40 84" style={style} aria-hidden>
      <defs>
        <radialGradient id="lamp-glow" cx="50%" cy="40%" r="60%">
          <stop offset="0" stopColor="#ffd98a" />
          <stop offset="1" stopColor="#c8892f" />
        </radialGradient>
      </defs>
      <ellipse cx="20" cy="81" rx="13" ry="2.4" fill="#000" opacity="0.3" />
      {/* base + stem */}
      <rect x="13" y="72" width="14" height="6" rx="2.5" fill="#3a2a18" />
      <rect x="18.5" y="40" width="3" height="34" rx="1.5" fill="#5a4326" />
      {/* warm glowing shade */}
      <path d="M8 40 L32 40 L27 14 L13 14 Z" fill="url(#lamp-glow)" />
      <path d="M8 40 L32 40 L27 14 L13 14 Z" fill="none" stroke="#7a5320" strokeWidth="1" />
      <ellipse cx="20" cy="50" rx="16" ry="9" fill="#ffce7a" opacity="0.22" />
    </svg>
  );
}

type BarSceneProps = {
  /** Dialogue shown in the speech bubble above the bartender's head. */
  message: React.ReactNode;
  /** Change this to replay the bubble's fade-in (e.g. on stage / mood change). */
  messageKey?: string;
  /** Content resting on the bar counter — cards, loader, CTA. */
  tray?: React.ReactNode;
};

export default function BarScene({ message, messageKey, tray }: BarSceneProps) {
  return (
    <div className="bar-scene">
      <div className="bar-scene__backbar" aria-hidden>
        <div className="bar-scene__lights">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="bar-scene__lamp" />
          ))}
        </div>
        {/* RUSH neon sign on the back wall, behind the bartender */}
        <div className="bar-scene__wall-logo">
          RUSH<span>BAR</span>
        </div>
        <div className="bar-scene__shelves">
          {SHELF_ROWS.map((row, r) => (
            <div key={r} className="bar-scene__shelf">
              <div className="bar-scene__shelf-items">
                {row.map((item, i) => (
                  <ShelfPiece key={i} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bar-scene__figure">
        <div className="ai-bubble bar-scene__bubble">
          <div className="ai-bubble__body">
            <div key={messageKey} className="ai-bubble__text">
              {message}
            </div>
            <span className="bar-scene__bubble-tail" aria-hidden />
          </div>
        </div>
        <BartenderHead variant="character" className="bar-scene__tender" size={320} />
      </div>

      <div className="bar-scene__counter" aria-hidden>
        <div className="bar-scene__counter-side bar-scene__counter-side--left">
          <TableLamp />
          {LEFT_COUNTER.map((b, i) => (
            <CounterBottle key={i} kind={b.kind} style={{ height: `${b.h}%` }} />
          ))}
        </div>
      </div>

      {tray != null && <div className="bar-scene__tray">{tray}</div>}
    </div>
  );
}
